import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * AppointmentForm
 * ─────────────────────────────────────────────────────────────────────────────
 * Reusable appointment form. Shares the exact same fields, validation, API
 * endpoint, and toast feedback as ContactSection — zero logic duplication.
 *
 * Props:
 *   onSuccess  – optional callback fired after a successful submission
 *                (e.g. close the parent modal after a short delay)
 * ─────────────────────────────────────────────────────────────────────────────
 */
const AppointmentForm = ({ onSuccess }) => {
  const { t, language } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    preferred_date: '',
  });

  // ── Validation (identical to ContactSection) ──────────────────────────────
  const validateForm = () => {
    const newErrors = {};

    // Name – letters & spaces, min 2 chars
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[A-Za-z\s]{2,}$/.test(formData.name)) {
      newErrors.name = 'Enter a valid name';
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone – Indian 10-digit format
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Enter valid 10-digit phone number';
    }

    // Message – optional, but min 10 chars if provided
    if (formData.message && formData.message.length < 10) {
      newErrors.message = 'Message should be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: '',
      preferred_date: '',
    });
    setErrors({});
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // ── Submit (same API endpoint as ContactSection) ──────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill out the form correctly.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post('https://bigbariatrics.com/api/send-mail', formData);
      toast.success(t('form_success'));
      resetForm();
      // Notify parent (modal) so it can close after a brief delay
      if (typeof onSuccess === 'function') {
        setTimeout(onSuccess, 1000);
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Field error helper ────────────────────────────────────────────────────
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-xs mt-1">{errors[field]}</p>
    ) : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Full Name */}
      <div>
        <Label htmlFor="modal-name" className="text-slate-700 font-medium">
          {t('form_name')}
        </Label>
        <Input
          id="modal-name"
          name="name"
          type="text"
          value={formData.name}
          onChange={handleChange}
          className="mt-1.5"
          placeholder={language === 'en' ? 'Enter your full name' : 'अपना पूरा नाम दर्ज करें'}
        />
        <FieldError field="name" />
      </div>

      {/* Email */}
      <div>
        <Label htmlFor="modal-email" className="text-slate-700 font-medium">
          {t('form_email')}
        </Label>
        <Input
          id="modal-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className="mt-1.5"
          placeholder={language === 'en' ? 'Enter your email' : 'अपना ईमेल दर्ज करें'}
        />
        <FieldError field="email" />
      </div>

      {/* Phone */}
      <div>
        <Label htmlFor="modal-phone" className="text-slate-700 font-medium">
          {t('form_phone')}
        </Label>
        <Input
          id="modal-phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          className="mt-1.5"
          placeholder="Enter Mobile Number"
        />
        <FieldError field="phone" />
      </div>

      {/* Preferred Date */}
      <div>
        <Label htmlFor="modal-preferred_date" className="text-slate-700 font-medium">
          {t('form_date')}
        </Label>
        <Input
          id="modal-preferred_date"
          name="preferred_date"
          type="date"
          value={formData.preferred_date}
          onChange={handleChange}
          className="mt-1.5"
          min={new Date().toISOString().split('T')[0]}
        />
      </div>

      {/* Message */}
      <div>
        <Label htmlFor="modal-message" className="text-slate-700 font-medium">
          {t('form_message')}
        </Label>
        <Textarea
          id="modal-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          className="mt-1.5"
          rows={3}
          placeholder={
            language === 'en' ? 'Tell us about your concern...' : 'अपनी चिंता के बारे में बताएं...'
          }
        />
        <FieldError field="message" />
      </div>

      {/* Submit */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 bg-[#800000] hover:bg-black text-white h-12 rounded-full font-bold text-base transition-all duration-300 shadow-lg"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Submitting…</span>
          </>
        ) : (
          <>
            <CheckCircle2 className="w-5 h-5" />
            <span>{t('form_submit')}</span>
          </>
        )}
      </Button>
    </form>
  );
};

export default AppointmentForm;
